/**
 * How a run card, the run's live view and the chat talk (PR-5a-3, 2026-09-25).
 *
 *   card link click (chat) ──RUN_LIVE_VIEW_EVENT──▶ RunLiveViewHost (opens the window)
 *   "done — carry on" (window) ──RUN_STEP_DONE_EVENT──▶ chat (resumes the thread)
 *
 * The chat intercepts a run card's link ONLY while a host is mounted to answer it
 * (`runLiveViewHostMounted`); otherwise the link keeps its old job and opens the run
 * on the /desktop page. No imports, so the chat engine, the page-level host and their
 * tests share it without loading anything behind it.
 *
 *   /desktop page's picker ──openWindow──▶ /desktop shell ──RUN_LIVE_VIEW_EVENT──▶ RunLiveViewHost
 *
 * PR-9c (2026-09-30): the /desktop page lists the windows a person can look at one at a time
 * (their own, and each task's window of its own), and a press asks the shell to open that one in
 * this same window, by its key (`readDesktopWindowOpen`).
 */

export const RUN_LIVE_VIEW_EVENT = 'prest0n:run-live-view';
export const RUN_STEP_DONE_EVENT = 'prest0n:run-step-done';

/** What the window needs, already validated by the card's own reader. */
export interface RunLiveViewRequest {
  sessionId: string;
  app: string;
  svc?: string;
  stopReason?: string;
  wall?: string;
  lang: 'en' | 'es';
  /** The thread the card was in, so "done — carry on" resumes that one. */
  threadId?: string | null;
  /** The card's outcome word (2026-09-29): a run still going (`running`) is one the person
   *  opened to watch, so the window's hold lets it carry on. */
  outcome?: string;
  /** A run still going: its own id (the workstation's run-id shape), the one run the window's
   *  hold lets carry on while the person watches. */
  runId?: string;
  /** PR-9c: which window to show, when the caller names it (the /desktop page's picker): the
   *  person's own (`front`) or a task's (`run:<id>`, running or kept after it finished). */
  windowKey?: RunLiveViewWindowKey;
  /** PR-9c: opened from the /desktop page's picker, to look at a window, not from a run's card. */
  from?: 'desktop-picker';
}

export type RunLiveViewWindowKey = 'front' | `run:${string}`;

/** The workstation's run-id shape (browser-service run-registry.ts RUN_ID_SHAPE,
 *  desktopRunCardHtml.ts DESKTOP_RUN_ID_RE), restated here so this module keeps no imports. */
const RUN_ID_RE = /^task_[a-z0-9]{1,12}_[a-z0-9]{1,8}$/;

export interface RunStepDoneDetail {
  sessionId: string;
  app: string;
  threadId?: string | null;
}

/** The browser-service's own session-id charset (desktopRunCardHtml.ts DESKTOP_SESSION_ID_RE). */
const SESSION_ID_RE = /^desktop-[a-z0-9-]{6,64}$/i;

let hosts = 0;

/** The host calls this on mount; the returned function undoes it on unmount. */
export function markRunLiveViewHost(): () => void {
  hosts += 1;
  let undone = false;
  return () => {
    if (undone) return;
    undone = true;
    hosts = Math.max(0, hosts - 1);
  };
}

/** Whether a host is mounted to open the window (else the card link keeps its job). */
export function runLiveViewHostMounted(): boolean {
  return hosts > 0;
}

/** Ask the host to open the window. False (and nothing dispatched) when no host is
 *  mounted or the request names no session — the caller lets the link do its job. */
export function requestRunLiveView(request: RunLiveViewRequest): boolean {
  if (!runLiveViewHostMounted() || typeof document === 'undefined') return false;
  if (!SESSION_ID_RE.test(request.sessionId)) return false;
  document.dispatchEvent(new CustomEvent(RUN_LIVE_VIEW_EVENT, { detail: request }));
  return true;
}

/** The request the host reads off the event, or null when it is not one. */
export function readRunLiveViewRequest(detail: unknown): RunLiveViewRequest | null {
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;
  const sessionId = typeof d.sessionId === 'string' ? d.sessionId.trim() : '';
  if (!SESSION_ID_RE.test(sessionId)) return null;
  const app = typeof d.app === 'string' && d.app.trim() ? d.app.trim().slice(0, 80) : 'the browser';
  return {
    sessionId,
    app,
    ...(typeof d.svc === 'string' && d.svc ? { svc: d.svc } : {}),
    ...(typeof d.stopReason === 'string' && d.stopReason ? { stopReason: d.stopReason } : {}),
    ...(typeof d.wall === 'string' && d.wall ? { wall: d.wall } : {}),
    lang: d.lang === 'es' ? 'es' : 'en',
    ...(typeof d.threadId === 'string' && d.threadId ? { threadId: d.threadId } : {}),
    ...(typeof d.outcome === 'string' && d.outcome ? { outcome: d.outcome.slice(0, 40) } : {}),
    ...(typeof d.runId === 'string' && RUN_ID_RE.test(d.runId) ? { runId: d.runId } : {}),
    ...(windowKeyOf(d.windowKey) ? { windowKey: windowKeyOf(d.windowKey) as RunLiveViewWindowKey } : {}),
    ...(d.from === 'desktop-picker' ? { from: 'desktop-picker' as const } : {}),
  };
}

/** A window key a view may be opened on, or null: `front`, or `run:` and the run-id shape. */
export function windowKeyOf(raw: unknown): RunLiveViewWindowKey | null {
  if (raw === 'front') return 'front';
  if (typeof raw !== 'string' || !raw.startsWith('run:')) return null;
  return RUN_ID_RE.test(raw.slice(4)) ? (raw as RunLiveViewWindowKey) : null;
}

/**
 * The /desktop page's picker asks for one window (PR-9c): `{source:'preston-realtime',
 * type:'openWindow', sessionId, key, app?, state?}`. The request the window opens with, or null
 * when the message is not one (a malformed session or key names nothing). A running task's window
 * lets that task carry on while the person watches it; a finished one's is the page it stopped on.
 */
export function readDesktopWindowOpen(data: unknown, lang: 'en' | 'es'): RunLiveViewRequest | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.source !== 'preston-realtime' || d.type !== 'openWindow') return null;
  const sessionId = typeof d.sessionId === 'string' ? d.sessionId.trim() : '';
  const key = windowKeyOf(d.key);
  if (!SESSION_ID_RE.test(sessionId) || !key) return null;
  const base = { sessionId, lang, windowKey: key, from: 'desktop-picker' as const };
  if (key === 'front') return { ...base, app: 'the browser' };
  const app = typeof d.app === 'string' && d.app.trim() ? d.app.trim().slice(0, 80) : 'the browser';
  return { ...base, app, runId: key.slice(4), ...(d.state === 'running' ? { outcome: 'running' } : {}) };
}

/** The window's "done — carry on": the chat picks the task back up in that thread. */
export function announceRunStepDone(detail: RunStepDoneDetail): void {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(RUN_STEP_DONE_EVENT, { detail }));
}

/** The line that resumes the thread after "done — carry on" — hidden, exactly as a finished
 *  sign-in resumes it (GlobalChatSystem's CONNECT_SIGNED_IN handler): the person's own
 *  click, sent in their words, so the primary carries on with the task. */
export function runStepDoneResumeText(app: string): string {
  return (
    `[system] I have done the step ${app} needed from me in Prest0n's browser. `
    + 'Carry on with the task from where it stopped, and do not ask me to do that step again.'
  );
}
